import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Dialog,
    TextField,
    CircularProgress,
    InputAdornment, DialogTitle, DialogContentText, DialogContent, DialogActions
} from '@mui/material';
import { 
    Close, 
    GetApp, 
    People, 
    TrendingUp, 
    EmojiObjects,
    MailOutline,
    Visibility,
    VisibilityOff
} from '@material-ui/icons';
import PoweredByDQ_Default from '../../../../assets/images/PoweredByDQ_Default.png';
import PoweredByDQ_White from '../../../../assets/images/PoweredByDQ_white.png';
import { useColorMode } from '../../../../context/colorMode/colorMode.context';
import { 
    loginUser, 
    getGoogleSignInLink, 
    persistGuestLoginUser, 
    persistGuestGetGoogleSignInLink 
} from '../../../../services/auth.service';
import { LOCAL_STORAGE_KEYS } from '../../../../utils/constants';
import { useAuth } from '../../../../context/auth';
import { save_e2ee_keys } from '../../../../utils/utils';

const GuestLoginDialogV2 = ({ open, onClose, onSuccess, onSignupClick }) => {
    const { authUser, setAuthToken, setAuthUser } = useAuth();
    const { isDark } = useColorMode();
    const history = useHistory();
    const PoweredByDQ_Logo = isDark ? PoweredByDQ_White : PoweredByDQ_Default;
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [pendingGuestAction, setPendingGuestAction] = useState(null);

    const handleAuthSuccess = (result) => {
        if (result.data && result.data.user) {
            setAuthToken(result.data.auth_token);
            setAuthUser(result.data.user);
            localStorage.setItem(LOCAL_STORAGE_KEYS.refresh_token, JSON.stringify(result.data.refresh_token));
            localStorage.setItem(LOCAL_STORAGE_KEYS.user_email, JSON.stringify(result.data.user.email));
            if (result.data.e2ee_pub_key) save_e2ee_keys(result);
            if (onSuccess) onSuccess();
            onClose();
        }
    };

    const handleGoogleSignIn = async () => {
        if (authUser?.is_guest) {
            setPendingGuestAction('google');
            setShowConfirmDialog(true);
            return;
        }
        setLoading(true);
        try {
            const result = await getGoogleSignInLink();
            localStorage.setItem(LOCAL_STORAGE_KEYS.dqpro_v2_google_auth_redirect, 'true');
            window.open(result.data.redirect_link, '_self');
        } catch (e) {
            setLoading(false);
        }
    };

    const handleContinueWithEmail = async () => {
        if (!email.trim() || !password.trim()) return;
        
        if (authUser?.is_guest) {
            setPendingGuestAction('email');
            setShowConfirmDialog(true);
            return;
        }

        setLoading(true);
        try {
            const result = await loginUser(email.trim(), password, false);
            handleAuthSuccess(result);
        } catch (e) {} finally {
            setLoading(false);
        }
    };

    const handleConfirmWarning = async () => {
        setShowConfirmDialog(false);
        setLoading(true);
        try {
            if (pendingGuestAction === 'google') {
                const result = await persistGuestGetGoogleSignInLink();
                window.open(result.data.redirect_link, '_self');
            } else {
                const result = await persistGuestLoginUser(email.trim(), password, false);
                handleAuthSuccess(result);
            }
        } catch (e) {
            setLoading(false);
        }
        setPendingGuestAction(null);
    };

    const features = [
        { icon: <GetApp style={{ color: '#F14A24', fontSize: 22 }} />, text: 'Save & access your program anytime' },
        { icon: <People style={{ color: '#F14A24', fontSize: 22 }} />, text: 'Share with colleagues & students' },
        { icon: <TrendingUp style={{ color: '#F14A24', fontSize: 22 }} />, text: 'Track student progress & results' },
        { icon: <EmojiObjects style={{ color: '#F14A24', fontSize: 22 }} />, text: 'Unlock premium features & resources' },
    ];

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            scroll="paper"
            PaperProps={{
                style: {
                    borderRadius: '20px',
                    padding: '0',
                    overflow: 'auto',
                    maxWidth: '480px',
                    maxHeight: '95vh',
                    boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
                    backgroundColor: 'var(--brand-surface, #fff)'
                }
            }}
        >
            <Box sx={{ position: 'relative', p: 2.5, pt: 3, textAlign: 'center', overflowY: 'auto' }}>
                <IconButton 
                    onClick={onClose} 
                    size="small"
                    style={{ position: 'absolute', right: 10, top: 10, color: 'var(--brand-text-muted, #999)' }}
                >
                    <Close fontSize="small" />
                </IconButton>

                <img 
                    src={PoweredByDQ_Logo} 
                    alt="Powered by DQ" 
                    style={{ height: '56px', marginBottom: '12px' }}
                />

                <Typography sx={{ fontSize: '24px', fontWeight: 900, mb: 1, color: 'var(--brand-text, #000)' }}>
                    Save Your Program
                </Typography>
                
                <Typography sx={{ fontSize: '14px', color: 'var(--brand-text-muted, #555)', mb: 1, px: 1, lineHeight: 1.4 }}>
                    Your AI literacy program is ready! Create a free account to save and unlock all features.
                </Typography>

                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#F14A24', mb: 0.5 }}>
                    No payment required until you want PRO features
                </Typography>

                <Typography sx={{ fontSize: '13px', mb: 2 }}>
                    <span
                        onClick={() => { onClose(); history.push('/pbdq/apply'); }}
                        style={{ color: '#1a6fbb', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}
                    >
                        Click here to explore the subscription packages
                    </span>
                </Typography>

                {/* Feature pills — horizontal wrap instead of vertical stack */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        mb: 2.5,
                        px: 1
                    }}
                >
                    {features.map((f, i) => (
                        <Box
                            key={i}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            {/* Icon */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'var(--brand-bg, #FFF5F2)',
                                    borderRadius: '8px',
                                    px: 1,
                                    py: 0.6,
                                }}
                            >
                                {f.icon}
                            </Box>

                            {/* Text */}
                            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'var(--brand-text, #444)' }}>
                                {f.text}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                <Box sx={{ px: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <Button 
                        variant="outlined"
                        onClick={handleGoogleSignIn}
                        disabled={loading}
                        fullWidth
                        sx={{ 
                            borderRadius: '12px', 
                            py: 1.2, 
                            textTransform: 'none', 
                            fontSize: '15px', 
                            fontWeight: 700,
                            borderColor: 'var(--brand-border, #ddd)',
                            color: 'var(--brand-text, #444)',
                            '&:hover': { borderColor: 'var(--brand-border, #bbb)', backgroundColor: 'var(--brand-surface, #fafafa)' }
                        }}
                        startIcon={<img src="https://www.gstatic.com/images/branding/product/1x/gsa_512dp.png" alt="Google" style={{ width: 20 }} />}
                    >
                        Continue with Google
                    </Button>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 1 }}>
                        <Box sx={{ flex: 1, height: '1px', backgroundColor: 'var(--brand-border, #eee)' }} />
                        <Typography sx={{ fontSize: '12px', color: 'var(--brand-text-muted, #bbb)', fontWeight: 700 }}>OR</Typography>
                        <Box sx={{ flex: 1, height: '1px', backgroundColor: 'var(--brand-border, #eee)' }} />
                    </Box>

                    <Box sx={{ textAlign: 'left' }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, mb: 0.5, color: 'var(--brand-text, #444)', ml: 0.5 }}>Email</Typography>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Enter your email address"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={loading}
                            sx={{
                                '& .MuiOutlinedInput-root': { borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)' },
                                '& .MuiOutlinedInput-input': { padding: '10px 14px', fontSize: '14px' },
                                mb: 1.5
                            }}
                        />
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, mb: 0.5, color: 'var(--brand-text, #444)', ml: 0.5 }}>Password</Typography>
                        <TextField
                            fullWidth
                            size="small"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Enter your password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={loading}
                            InputProps={{
                                endAdornment: (
                                    <IconButton onClick={() => setShowPassword(!showPassword)} size="small">
                                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </IconButton>
                                )
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': { borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)' },
                                '& .MuiOutlinedInput-input': { padding: '10px 14px', fontSize: '14px' },
                                mb: 2
                            }}
                        />
                    </Box>

                    <Button 
                        variant="contained"
                        onClick={handleContinueWithEmail}
                        disabled={loading || !email.trim() || !password.trim()}
                        fullWidth
                        sx={{ 
                            borderRadius: '12px', 
                            py: 1.4, 
                            textTransform: 'none', 
                            fontSize: '15px', 
                            fontWeight: 800,
                            background: 'linear-gradient(90deg, #F14A24, #FF7043)',
                            '&:hover': { background: 'linear-gradient(90deg, #D13A14, #F4511E)' }
                        }}
                        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <MailOutline fontSize="small" />}
                    >
                        {loading ? 'Logging in...' : 'Continue with Email'}
                    </Button>

                    <Typography sx={{ fontSize: '15px', color: 'var(--brand-text-muted, #666)', mt: 1, mb: 1 }}>
                        Don't have an account? <span
                            style={{ color: 'var(--brand-text, #000)', fontWeight: 800, textDecoration: 'underline', cursor: 'pointer' }}
                            onClick={onSignupClick}
                        >Sign up</span>
                    </Typography>

                    <Typography 
                        onClick={onClose}
                        sx={{ 
                            fontSize: '14px', 
                            fontWeight: 700, 
                            color: 'var(--brand-text-muted, #777)',
                            cursor: 'pointer',
                            mt: 1, 
                            mb: 1,
                            '&:hover': { textDecoration: 'underline' } 
                        }}
                    >
                        Continue as Guest (Limited Features)
                    </Typography>
                </Box>
            </Box>

            {/* Merge Warning */}
            <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)} PaperProps={{ style: { borderRadius: 14, backgroundColor: 'var(--brand-surface, #fff)' } }}>
                <DialogTitle sx={{ fontWeight: 700, color: 'var(--brand-text, #000)' }}>Heads up!</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: 'var(--brand-text-muted, #555)' }}>
                        Logging in will merge your guest session. You might lose your current program in progress.
                    </DialogContentText>
                </DialogContent>
                <DialogActions style={{ padding: '8px 20px 20px' }}>
                    <Button onClick={() => setShowConfirmDialog(false)} sx={{ color: 'var(--brand-text-muted, #555)', textTransform: 'none' }}>Cancel</Button>
                    <Button onClick={handleConfirmWarning} variant="contained" style={{ backgroundColor: '#0397a6', color: '#fff', textTransform: 'none' }}>Yes, Log In</Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

export default GuestLoginDialogV2;
